import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import VideoIcon from 'components/icons/Video';
import { useHistory } from 'react-router-dom';
import { formatWebinarTimeRange } from 'utils/dateFormatters';
import { Webinar, WebinarType } from 'models/webinars';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { useTranslation } from 'react-i18next';
import Routes from '../../Routes';

const WebinarItemContainer = styled.div<{ $isCurrent: boolean }>`
  position: relative;
  padding: 16px 56px 16px 24px;
  align-items: center;
  background-color: ${({ $isCurrent }) => ($isCurrent ? '#F4F8FF' : '#FFFFFF')};
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: ${({ $isCurrent }) => ($isCurrent ? '#F4F8FF' : '#f7faff')};
  }
`;

const WebinarTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const WebinarTitle = styled.div`
  font-family: Lato;
  font-size: 16px;
  line-height: 22px;
`;

const WebinarTime = styled.div`
  font-size: 14px;
  line-height: 17px;
  color: #A2AAB3;
  white-space: nowrap;
`;

const WebinarStatusIndicatorContainer = styled.div`
  margin: 10px 0;
`;

const GenericIndicator = styled.span`
  padding: 2px 8px;
  margin: 10px 0;
  height: 18px;
  border-radius: 2px;

  text-transform: uppercase;
  font-family: Lato;
  font-size: 12px;
  font-weight: bold;
  line-height: 14px;
`;

const LiveIndicator = styled(GenericIndicator)`
  background: #F77;
  color: #FFF;
`;

const ComingIndicator = styled(GenericIndicator)`
  background: #FFF;
  border: 1px solid #52B988;
  color: #52B988;
`;

const VideoIconContainer = styled.div`
  position: absolute;
  right: 17.5px;
  top: calc(50% - 10.5px);

  > svg {
    height: 21px;
    width: 21px;
  }
`;

const WebinarStatusIndicator = ({ webinar }: { webinar: Webinar }) => {
  const { t } = useTranslation();
  const indicators = {
    onDemand: (
      <VideoIconContainer>
        <VideoIcon />
      </VideoIconContainer>
    ),
    live: (
      <WebinarStatusIndicatorContainer>
        <LiveIndicator>{t('webinar.live', 'Live')}</LiveIndicator>
      </WebinarStatusIndicatorContainer>
    ),
    coming: (
      <WebinarStatusIndicatorContainer>
        <ComingIndicator>{t('webinar.coming', 'Coming')}</ComingIndicator>
      </WebinarStatusIndicatorContainer>
    ),
  };

  const [showing, setShowing] = useState(indicators.onDemand);
  useEffect(() => {
    const update = () => {
      if (webinar.kind === WebinarType.Live) {
        if (moment(webinar.startAt).isAfter(moment.now())) {
          setShowing(indicators.coming);
        } else if (moment(webinar.endAt).isAfter(moment.now())) {
          setShowing(indicators.live);
        } else {
          setShowing(indicators.onDemand);
        }
      } else {
        setShowing(indicators.onDemand);
      }
    };
    update();
    const taskId = setInterval(() => update(), 1000);
    return () => clearInterval(taskId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return showing;
};

export interface FollowingWebinarItemProps {
  webinar: Webinar;
  isCurrent: boolean;
}

const FollowingWebinarItem: FC<FollowingWebinarItemProps> = ({ webinar, isCurrent }) => {
  const history = useHistory();
  const locale = useSelector(selectLocale);
  const gotoWebinar = () => history.push(Routes.webinarSession(webinar.id));

  return (
    <WebinarItemContainer $isCurrent={isCurrent} onClick={gotoWebinar}>
      <WebinarTitleContainer>
        <WebinarTitle>{webinar?.translations?.[locale]?.title}</WebinarTitle>
      </WebinarTitleContainer>
      <WebinarStatusIndicator webinar={webinar} />
      {webinar.startAt && webinar.kind === 'time_framed'
      && <WebinarTime>{formatWebinarTimeRange(webinar.startAt, webinar.endAt)}</WebinarTime>}
    </WebinarItemContainer>
  );
};

export default FollowingWebinarItem;
